import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'modularoom_admin_token';
const TOKEN_EXP_KEY = 'modularoom_admin_token_exp';

// ---------- Formatters ----------
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days} d ago`;
  return fmtDate(iso);
};

// ---------- Login screen ----------
function Login({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Login failed');
      }
      const data = await res.json();
      const exp = Date.now() + (data.expires_in || 43200) * 1000;
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(TOKEN_EXP_KEY, String(exp));
      onSuccess(data.token);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md bg-white p-10 border border-black/10">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">MODULAROOM · Admin</div>
        <h1 className="mt-4 font-display text-3xl tracking-tight">Enquiries dashboard</h1>
        <p className="mt-3 text-[13px] text-black/55 leading-relaxed">
          Restricted access. Enter the admin password to view website enquiries.
        </p>
        <label className="block mt-8">
          <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-2">Password</div>
          <input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-black/15 focus:border-[#C7A36A] focus:outline-none py-3 text-[17px] tracking-tight"
          />
        </label>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !password}
          className="mt-8 w-full rounded-full bg-[#111] text-white py-3.5 text-[12px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors disabled:opacity-40"
        >
          {loading ? 'Signing in…' : 'Enter Dashboard'}
        </button>
      </form>
    </main>
  );
}

// ---------- Dashboard ----------
function Dashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | unread | read
  const [projectType, setProjectType] = useState('all');
  const [active, setActive] = useState(null);
  const [emailProvider, setEmailProvider] = useState('disabled');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, healthRes] = await Promise.all([
        fetch(`${API}/api/admin/consultations`, { headers: authHeaders }),
        fetch(`${API}/api/health`),
      ]);
      if (listRes.status === 401) {
        onLogout();
        return;
      }
      if (!listRes.ok) throw new Error('Failed to load enquiries');
      const data = await listRes.json();
      setItems(data);
      const health = await healthRes.json().catch(() => ({}));
      setEmailProvider(health.email_provider || 'disabled');
    } catch (err) {
      toast.error(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onLogout]);

  useEffect(() => { load(); }, [load]);

  const projectTypes = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.project_type && s.add(i.project_type));
    return ['all', ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === 'unread' && it.read) return false;
      if (filter === 'read' && !it.read) return false;
      if (projectType !== 'all' && it.project_type !== projectType) return false;
      if (!q) return true;
      return [it.name, it.email, it.phone, it.company, it.country, it.project_type, it.message]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, search, filter, projectType]);

  const unreadCount = items.filter((i) => !i.read).length;

  const toggleRead = async (item, next) => {
    // optimistic
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: next } : i)));
    if (active && active.id === item.id) setActive({ ...active, read: next });
    try {
      const res = await fetch(`${API}/api/admin/consultations/${item.id}/read`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ read: next }),
      });
      if (!res.ok) throw new Error('Update failed');
    } catch (err) {
      toast.error(err.message || 'Update failed');
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: !next } : i)));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/api/admin/consultations.csv`, { headers: authHeaders });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `modularoom-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    }
  };

  const providerBadge = {
    resend: { label: 'Resend', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    gmail_smtp: { label: 'Gmail SMTP', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
    disabled: { label: 'Email disabled', tone: 'bg-red-50 text-red-700 border-red-200' },
  }[emailProvider] || { label: emailProvider, tone: 'bg-black/5 text-black/70 border-black/10' };

  return (
    <main className="min-h-screen bg-[#F5F5F5] text-[#111]" data-testid="admin-dashboard">
      <Helmet><title>Enquiries · MODULAROOM Admin</title></Helmet>

      {/* Header */}
      <header className="bg-white border-b border-black/10 sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-6 min-w-0">
            <div className="font-display text-lg tracking-[0.28em]">MODULAROOM</div>
            <div className="hidden md:block text-[10px] tracking-[0.32em] uppercase text-black/45">Admin · Enquiries</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`hidden md:inline-flex items-center rounded-full border px-3 py-1 text-[10px] tracking-[0.22em] uppercase ${providerBadge.tone}`} data-testid="provider-badge">
              {providerBadge.label}
            </span>
            <button onClick={load} className="text-[11px] tracking-[0.22em] uppercase text-black/60 hover:text-[#C7A36A] transition-colors" data-testid="refresh-btn">Refresh</button>
            <button onClick={onLogout} className="text-[11px] tracking-[0.22em] uppercase text-black/60 hover:text-[#C7A36A] transition-colors" data-testid="logout-btn">Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total enquiries" value={items.length} testid="stat-total" />
          <StatCard label="Unread" value={unreadCount} accent testid="stat-unread" />
          <StatCard label="Emails delivered" value={items.filter((i) => i.email_sent).length} testid="stat-sent" />
          <StatCard label="Last 7 days" value={items.filter((i) => (Date.now() - new Date(i.created_at).getTime()) < 7 * 86400000).length} testid="stat-recent" />
        </div>

        {/* Journal broadcast */}
        <JournalBroadcast authHeaders={authHeaders} />

        {/* Toolbar */}
        <div className="bg-white border border-black/10 p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center mb-6">
          <input
            data-testid="admin-search"
            placeholder="Search name, email, phone, message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 bg-transparent border border-black/10 focus:border-[#C7A36A] focus:outline-none px-4 py-2.5 text-[14px]"
          />
          <div className="flex gap-2">
            {['all', 'unread', 'read'].map((v) => (
              <button
                key={v}
                data-testid={`filter-${v}`}
                onClick={() => setFilter(v)}
                className={`rounded-full px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                  filter === v ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-[#111] border-black/15 hover:border-[#C7A36A]'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <select
            data-testid="filter-project-type"
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
            className="bg-white border border-black/15 px-4 py-2.5 text-[13px] focus:outline-none focus:border-[#C7A36A]"
          >
            {projectTypes.map((t) => (
              <option key={t} value={t}>{t === 'all' ? 'All project types' : t}</option>
            ))}
          </select>
          <button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="rounded-full bg-[#111] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors"
          >
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-black/10 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-black/50 text-[13px] tracking-[0.22em] uppercase">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-black/50">
              <div className="font-display text-2xl tracking-tight mb-2">No enquiries match your filters.</div>
              <div className="text-[13px]">Try clearing search or filter.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]" data-testid="enquiries-table">
                <thead className="bg-[#FAFAFA] text-black/50 text-[10px] tracking-[0.28em] uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left w-8"></th>
                    <th className="px-4 py-3 text-left">Received</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Project</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Message</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`row-${it.id}`}
                      className={`border-t border-black/5 cursor-pointer hover:bg-[#FAFAFA] transition-colors ${!it.read ? 'bg-[#FFFBF3]' : ''}`}
                      onClick={() => setActive(it)}
                    >
                      <td className="px-4 py-4 align-top">
                        <span className={`inline-block w-2 h-2 rounded-full ${it.read ? 'bg-black/15' : 'bg-[#C7A36A]'}`} />
                      </td>
                      <td className="px-4 py-4 align-top whitespace-nowrap">
                        <div className="text-[13px] text-black/80">{fmtDate(it.created_at)}</div>
                        <div className="text-[11px] text-black/45 mt-0.5">{relativeTime(it.created_at)}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className={`${!it.read ? 'font-medium' : ''} text-[14px]`}>{it.name}</div>
                        {it.company && <div className="text-[12px] text-black/50 mt-0.5">{it.company}</div>}
                        <div className="text-[11px] text-black/40 mt-0.5">{it.country}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <a href={`mailto:${it.email}`} onClick={(e) => e.stopPropagation()} className="text-[13px] text-black/80 hover:text-[#C7A36A]">{it.email}</a>
                        <div className="text-[12px] text-black/55 mt-0.5">{it.phone}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className="inline-block rounded-full bg-black/5 text-black/70 px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase">{it.project_type}</span>
                      </td>
                      <td className="px-4 py-4 align-top hidden lg:table-cell max-w-md">
                        <div className="text-[13px] text-black/70 line-clamp-2">{it.message}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-right whitespace-nowrap">
                        <button
                          data-testid={`mark-read-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); toggleRead(it, !it.read); }}
                          className="text-[11px] tracking-[0.18em] uppercase text-black/60 hover:text-[#C7A36A] transition-colors"
                        >
                          {it.read ? 'Mark unread' : 'Mark read'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 z-50" data-testid="enquiry-drawer" onClick={() => setActive(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 h-full w-full md:w-[540px] bg-white overflow-y-auto shadow-2xl"
          >
            <div className="p-8 md:p-10">
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Enquiry</div>
                  <h2 className="mt-3 font-display text-3xl tracking-tight">{active.name}</h2>
                  {active.company && <div className="text-[14px] text-black/60 mt-1">{active.company}</div>}
                </div>
                <button onClick={() => setActive(null)} data-testid="drawer-close" className="text-black/50 hover:text-[#111] text-2xl leading-none">×</button>
              </div>

              <Row label="Received" value={fmtDate(active.created_at)} />
              <Row label="Email" value={<a href={`mailto:${active.email}`} className="hover:text-[#C7A36A]">{active.email}</a>} />
              <Row label="Phone" value={<a href={`tel:${active.phone}`} className="hover:text-[#C7A36A]">{active.phone}</a>} />
              <Row label="Country" value={active.country} />
              <Row label="Project type" value={active.project_type} />
              <Row label="Notification email" value={active.email_sent ? `Delivered via ${active.email_provider || 'provider'}` : 'Not sent'} />

              <div className="mt-8 pt-6 border-t border-black/10">
                <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-3">Message</div>
                <div className="text-[15px] leading-relaxed text-black/80 whitespace-pre-wrap">{active.message}</div>
              </div>

              <div className="mt-10 flex gap-3">
                <a
                  href={`mailto:${active.email}?subject=Re: Your MODULAROOM enquiry&body=Hi ${encodeURIComponent(active.name)}%2C%0A%0AThanks for your enquiry.`}
                  className="flex-1 text-center rounded-full bg-[#111] text-white py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors"
                >Reply by email</a>
                <button
                  onClick={() => toggleRead(active, !active.read)}
                  data-testid="drawer-toggle-read"
                  className="rounded-full border border-black/15 px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:border-[#C7A36A] hover:text-[#C7A36A] transition-colors"
                >{active.read ? 'Mark unread' : 'Mark read'}</button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}

const StatCard = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className={`p-6 border ${accent ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-[#111] border-black/10'}`}>
    <div className={`text-[10px] tracking-[0.32em] uppercase ${accent ? 'text-[#C7A36A]' : 'text-black/45'}`}>{label}</div>
    <div className="mt-4 font-display text-4xl tracking-tight tabular-nums">{value}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-6 py-3 border-b border-black/10">
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 pt-1 whitespace-nowrap">{label}</div>
    <div className="text-[14px] text-black/85 text-right">{value}</div>
  </div>
);

// ---------- Journal Broadcast ----------
const CS25_HERO = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_modularoom-design/artifacts/su1kq50b_Capsule%20House%20MODULAROOM%20CS25%20Exterior.jpeg';

const DEFAULT_ARTICLE = {
  slug: 'cs25-capsule-house-india-resorts',
  article_title: 'CS25: A Capsule House for Resort Hospitality in India',
  article_url: 'https://modularoom.com/journal/cs25-capsule-house-india-resorts',
  article_excerpt: "Meet Modularoom's hospitality-focused Capsule Suite. One bedroom, one bathroom and a private balcony — designed as a repeatable accommodation product for resorts, retreats and destination hospitality.",
  article_image: CS25_HERO,
};

function JournalBroadcast({ authHeaders }) {
  const [expanded, setExpanded] = useState(false);
  const [insights, setInsights] = useState(null);
  const [history, setHistory] = useState([]);
  const [articles, setArticles] = useState([]);
  const [mode, setMode] = useState('broadcast'); // 'broadcast' | 'publish'
  const [form, setForm] = useState({
    ...DEFAULT_ARTICLE,
    subject: '',
    preview_text: '',
    test_email: '',
    auto_broadcast: true,
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [ins, h, arts] = await Promise.all([
        fetch(`${API}/api/admin/newsletter/insights`, { headers: authHeaders }).then((r) => r.json()),
        fetch(`${API}/api/admin/newsletter/broadcasts`, { headers: authHeaders }).then((r) => r.json()),
        fetch(`${API}/api/admin/journal/articles`, { headers: authHeaders }).then((r) => r.json()),
      ]);
      setInsights(ins);
      setHistory(Array.isArray(h) ? h : []);
      setArticles(Array.isArray(arts) ? arts : []);
    } catch (_) { /* silent */ }
  }, [authHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  const totalSubs = insights?.total_subscribers ?? 0;

  const submitBroadcast = async (asTest) => {
    if (!form.article_title || !form.article_url || !form.article_excerpt || !form.article_image) {
      toast.error('Please fill in title, URL, excerpt and image.');
      return;
    }
    if (asTest && !form.test_email) {
      toast.error('Please enter a test email address.');
      return;
    }
    if (!asTest && totalSubs === 0) {
      toast.error('No subscribers to send to yet.');
      return;
    }
    if (!asTest && !window.confirm(`Send this article to ${totalSubs} subscribers?`)) return;

    setBusy(true);
    try {
      const body = {
        article_title: form.article_title, article_url: form.article_url,
        article_excerpt: form.article_excerpt, article_image: form.article_image,
        subject: form.subject, preview_text: form.preview_text,
      };
      if (asTest) body.test_email = form.test_email;
      const res = await fetch(`${API}/api/admin/newsletter/broadcast`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const data = await res.json();
      toast.success(asTest
        ? `Test sent to ${form.test_email} (${data.sent}/${data.recipients_total})`
        : `Broadcast complete · ${data.sent}/${data.recipients_total} delivered`);
      refresh();
    } catch (err) { toast.error(err.message || 'Broadcast failed'); }
    finally { setBusy(false); }
  };

  const submitPublish = async () => {
    if (!form.slug || !form.article_title || !form.article_url || !form.article_excerpt || !form.article_image) {
      toast.error('Please fill in slug, title, URL, excerpt and image.');
      return;
    }
    if (form.auto_broadcast && totalSubs > 0
        && !window.confirm(`Publish "${form.article_title}" and auto-broadcast to ${totalSubs} subscribers?`)) return;

    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/journal/articles`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: form.slug.trim().toLowerCase(),
          title: form.article_title, url: form.article_url,
          excerpt: form.article_excerpt, image: form.article_image,
          subject: form.subject, preview_text: form.preview_text,
          auto_broadcast: form.auto_broadcast,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const data = await res.json();
      if (data.already_broadcast) {
        toast.success('Article updated. Already broadcast previously — not sending again.');
      } else if (data.broadcast) {
        toast.success(`Published & broadcast · ${data.broadcast.sent}/${data.broadcast.recipients_total} delivered`);
      } else {
        toast.success('Article published. Broadcast skipped.');
      }
      refresh();
    } catch (err) { toast.error(err.message || 'Publish failed'); }
    finally { setBusy(false); }
  };

  const deliveryPct = insights?.delivery_rate_pct;

  return (
    <div data-testid="journal-broadcast" className="bg-white border border-black/10 mb-6">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FAFAFA] transition-colors"
      >
        <div className="flex items-baseline gap-4 flex-wrap">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Journal Newsletter</div>
          <div className="text-[13px] text-black/60">
            <span className="tabular-nums font-medium text-black/85" data-testid="broadcast-subscriber-count">{totalSubs}</span> subscribers
            {insights?.growth_7d > 0 && <span className="ml-2 text-[#0a7a3b]">+{insights.growth_7d} in 7d</span>}
            {' · '}{history.length} broadcasts{deliveryPct != null && <span className="ml-2 text-black/40">· {deliveryPct.toFixed(0)}% delivered</span>}
          </div>
        </div>
        <div className={`text-[11px] tracking-[0.24em] uppercase text-black/50 transition-transform ${expanded ? 'rotate-180' : ''}`}>▾</div>
      </button>

      {expanded && (
        <div className="border-t border-black/10 p-5 md:p-6 space-y-6">
          {insights && <InsightsTiles insights={insights} />}

          {/* Mode toggle */}
          <div className="inline-flex border border-black/15 p-0.5 rounded-full">
            <button
              type="button"
              onClick={() => setMode('broadcast')}
              data-testid="mode-broadcast"
              className={`px-4 py-1.5 text-[10px] tracking-[0.22em] uppercase rounded-full transition-colors ${mode === 'broadcast' ? 'bg-[#111] text-white' : 'text-black/60 hover:text-black'}`}
            >Send broadcast</button>
            <button
              type="button"
              onClick={() => setMode('publish')}
              data-testid="mode-publish"
              className={`px-4 py-1.5 text-[10px] tracking-[0.22em] uppercase rounded-full transition-colors ${mode === 'publish' ? 'bg-[#111] text-white' : 'text-black/60 hover:text-black'}`}
            >Publish new article</button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {mode === 'publish' && (
              <TextField label="URL slug (idempotent)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="cs25-capsule-house-india-resorts" testid="bc-slug" />
            )}
            <TextField label="Article title" value={form.article_title} onChange={(v) => setForm({ ...form, article_title: v })} testid="bc-title" />
            <TextField label="Article URL" value={form.article_url} onChange={(v) => setForm({ ...form, article_url: v })} testid="bc-url" />
            <TextField label="Hero image URL" value={form.article_image} onChange={(v) => setForm({ ...form, article_image: v })} testid="bc-image" />
            <TextField label="Subject line (optional)" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} placeholder="Modularoom Journal · <title>" testid="bc-subject" />
          </div>
          <label className="block">
            <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-2">Excerpt (shown in the email body)</div>
            <textarea
              rows={3}
              value={form.article_excerpt}
              onChange={(e) => setForm({ ...form, article_excerpt: e.target.value })}
              data-testid="bc-excerpt"
              className="w-full bg-white border border-black/15 focus:border-[#C7A36A] focus:outline-none px-4 py-2.5 text-[14px] leading-relaxed"
            />
          </label>

          {mode === 'broadcast' ? (
            <div className="grid md:grid-cols-2 gap-4 items-end pt-2 border-t border-black/10">
              <TextField label="Test email (optional)" value={form.test_email} onChange={(v) => setForm({ ...form, test_email: v })} placeholder="you@modularoom.com" testid="bc-testemail" />
              <div className="flex gap-3 md:justify-end">
                <button onClick={() => submitBroadcast(true)} disabled={busy} data-testid="bc-send-test" className="rounded-full border border-black/25 text-black/85 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors disabled:opacity-40">Send test</button>
                <button onClick={() => submitBroadcast(false)} disabled={busy || totalSubs === 0} data-testid="bc-send-all" className="rounded-full bg-[#111] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors disabled:opacity-40">{busy ? 'Sending…' : `Broadcast to ${totalSubs}`}</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 items-center pt-2 border-t border-black/10">
              <label className="inline-flex items-center gap-2 text-[13px] text-black/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.auto_broadcast}
                  onChange={(e) => setForm({ ...form, auto_broadcast: e.target.checked })}
                  data-testid="bc-auto-broadcast"
                  className="accent-[#C7A36A] w-4 h-4"
                />
                <span>Auto-broadcast to {totalSubs} subscribers on publish</span>
              </label>
              <div className="flex-1" />
              <button
                onClick={submitPublish}
                disabled={busy}
                data-testid="bc-publish"
                className="rounded-full bg-[#111] text-white px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors disabled:opacity-40"
              >{busy ? 'Publishing…' : 'Publish article'}</button>
            </div>
          )}

          {articles.length > 0 && (
            <div className="pt-4 border-t border-black/10">
              <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-3">Registered Journal articles</div>
              <div className="space-y-1">
                {articles.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-baseline justify-between gap-4 text-[13px] py-1.5 border-b border-black/5 last:border-0">
                    <span className="truncate text-black/80">{a.title}</span>
                    <span className="text-black/50 tabular-nums whitespace-nowrap">
                      {a.broadcast_stats ? `${a.broadcast_stats.sent}/${a.broadcast_stats.recipients_total}` : 'not sent'} · {fmtDate(a.published_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="pt-4 border-t border-black/10">
              <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-3">Recent broadcasts</div>
              <div className="space-y-1">
                {history.slice(0, 5).map((b) => (
                  <div key={b.id} className="flex items-baseline justify-between gap-4 text-[13px] py-1.5 border-b border-black/5 last:border-0">
                    <span className="truncate text-black/80">{b.article_title}{b.is_test ? ' · test' : ''}</span>
                    <span className="text-black/50 tabular-nums whitespace-nowrap">{b.sent}/{b.recipients_total} · {fmtDate(b.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Insights tiles + sparkline ----------
function InsightsTiles({ insights }) {
  const buckets = insights.weekly_buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const w = 220, h = 46, pad = 2;
  const bw = buckets.length ? (w - pad * 2) / buckets.length : 0;
  const barH = (c) => Math.max(1, (c / max) * (h - pad * 2));

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <InsightTile label="Total subscribers" value={insights.total_subscribers} testid="ins-total" />
      <InsightTile label="Last 7 days" value={`+${insights.growth_7d}`} accent={insights.growth_7d > 0} testid="ins-7d" />
      <InsightTile label="Last 30 days" value={`+${insights.growth_30d}`} accent={insights.growth_30d > 0} testid="ins-30d" />
      <InsightTile
        label="Delivery rate"
        value={insights.delivery_rate_pct == null ? '—' : `${insights.delivery_rate_pct.toFixed(0)}%`}
        hint={insights.broadcasts_recipients ? `${insights.broadcasts_sent}/${insights.broadcasts_recipients}` : 'No broadcasts yet'}
        testid="ins-delivery"
      />
      <div className="col-span-2 md:col-span-4 border border-black/10 p-4">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] tracking-[0.28em] uppercase text-black/45">Subscriber growth · last 8 weeks</div>
          <div className="text-[11px] text-black/40 tabular-nums">peak {max}</div>
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} className="mt-2 w-full max-w-[380px] h-[46px]" role="img" aria-label="Weekly subscribers">
          {buckets.map((b, i) => {
            const bh = barH(b.count);
            return (
              <rect
                key={b.week_end}
                x={pad + i * bw + 1}
                y={h - pad - bh}
                width={Math.max(1, bw - 2)}
                height={bh}
                fill={i === buckets.length - 1 ? '#C7A36A' : '#111'}
                opacity={b.count === 0 ? 0.15 : 0.85}
              />
            );
          })}
        </svg>
        {buckets.length > 0 && (
          <div className="mt-1 flex justify-between text-[9px] tracking-[0.16em] text-black/35 max-w-[380px]">
            <span>{buckets[0]?.week_end}</span>
            <span>{buckets[buckets.length - 1]?.week_end}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const InsightTile = ({ label, value, hint, accent, testid }) => (
  <div data-testid={testid} className={`border p-4 ${accent ? 'border-[#C7A36A]/60 bg-[#FAF6EE]' : 'border-black/10 bg-white'}`}>
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45">{label}</div>
    <div className={`mt-2 text-[22px] tabular-nums ${accent ? 'text-[#C7A36A]' : 'text-black/85'}`}>{value}</div>
    {hint && <div className="mt-1 text-[11px] text-black/45">{hint}</div>}
  </div>
);

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-2">{label}</div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-white border border-black/15 focus:border-[#C7A36A] focus:outline-none px-4 py-2.5 text-[14px]"
    />
  </label>
);

// ---------- Page shell ----------
export default function AdminEnquiries() {
  const [token, setToken] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) || 0);
    if (t && exp > Date.now()) setToken(t);
    else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXP_KEY);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXP_KEY);
    setToken(null);
  }, []);

  return token
    ? <Dashboard token={token} onLogout={handleLogout} />
    : <Login onSuccess={setToken} />;
}
